/* eslint-disable max-len */
import React, { useState, useEffect } from "react";
import * as Axios from "providers/axios";
import * as User from "providers/user";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PersonIcon,
  SalesIcon,
  ServiceDetailsIcon
} from "assets/Icons";
import TitleWithImage from "./TitleWithImage";
import DashboardAppointmentList from "./DashboardAppointmentList";
import DashboardArtists from "./DashboardArtists";
import { AnimatedLoader } from "./AnimatedLoader";
import { IconButton, Popover } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import DateSelector from "./DateSelector";
import { useTranslate } from "react-admin";
import { formatCurrency } from "utils/helpers";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export default function DashboardHome() {
  const { api } = Axios.useAxios();
  const { user } = User.useUser();
  const [company, setCompany] = React.useState<any>();

  useEffect(() => {
    (async function () {
      try {
        if (user?.impersonate_company_id) {
          const response = await api
            .get(`/v1/companies/impersonate/${user.impersonate_company_id}`)
            .then((r) => r.data);
          if (response?.id) {
            setCompany(response);
          }
        } else if (user?.company?.slug || user?.joined_company?.slug) {
          const slug = user?.company?.slug || user?.joined_company?.slug;
          const response = await api
            .get(`/v1/companies/${slug}`)
            .then((r) => r.data);

          if (response?.id) {
            setTimeout(() => {
              setCompany(response);
            }, 1000);
          }
        }
      } catch (err) {
        return;
      }
    })();
  }, [
    user?.company?.slug,
    api,
    setCompany,
    user?.joined_company?.slug,
    user?.impersonate_company_id
  ]);

  const locationIds = company?.locations?.map((location: any) => location.id);

  if (!company) return <AnimatedLoader withTranslation />;

  const timezone = company?.locations?.[0]?.timezone || "PST8PDT";

  return <DashboardContent timezone={timezone} locationIds={locationIds} />;
}

function DashboardContent({
  timezone,
  locationIds
}: {
  timezone: string;
  locationIds: string[];
}) {
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const [data, setData] = useState<any>(null);
  // const storedDateStr = localStorage.getItem("DashboardDateFilter") || null;
  const storedDateStr = null;
  let defaultDate = dayjs().tz(timezone, true);
  if (storedDateStr) {
    defaultDate = dayjs(storedDateStr, "YYYY-MM-DD").tz(timezone, true);
  }

  const [date, setDate] = useState<Dayjs | null>(defaultDate);

  const fetchDashboardData = async () => {
    try {
      const response = await api.post(`/v1/analytics`, {
        start_time: date?.tz(timezone, true).startOf("day").toISOString(),
        end_time: date?.tz(timezone, true).endOf("day").toISOString(),
        location_ids: locationIds
      });

      setData(response.data);
    } catch (error) {
      // Do nothing
    }
  };

  const setAndStoreDate = (newDate: Dayjs) => {
    const tzDate = newDate.tz(timezone, true);
    setDate(tzDate);
    localStorage.setItem("DashboardDateFilter", tzDate.format("YYYY-MM-DD"));
  };

  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const handleDatePopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };
  const handleDatePopoverClose = () => {
    setDateAnchorEl(null);
  };
  const datePopoverOpen = Boolean(dateAnchorEl);
  const datePopoverId = datePopoverOpen ? "simple-date-popover" : undefined;

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    localStorage.removeItem("DashboardDateFilter");
  }, []);

  if (data === null) return <AnimatedLoader withTranslation />;

  return (
    <div className="w-full max-w-[1152px] h-[calc(100dvh-65px)] lg:h-screen overflow-y-auto mx-auto pb-5 pl-2 pr-2 lg:pl-0 lg:pr-4">
      <div className="w-full h-full flex flex-col gap-6">
        <div className="w-full flex justify-between items-center mt-4">
          <h4 className="font-['OptimaLTStd'] text-[24px] text-[rgba(48,47,46,0.95)] font-[600] leading-8">
            {translate("dashboard.dashboard")}
          </h4>
          <div className="flex border border-[rgba(48,47,46,0.23)] bg-[#FAF9F7] rounded-[32px]">
            <IconButton
              sx={{
                borderRadius: "0px",
                borderTopLeftRadius: "32px",
                borderBottomLeftRadius: "32px"
              }}
              className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
              onClick={() => {
                if (date) {
                  const newDate = date.subtract(1, "day");
                  setAndStoreDate(newDate);
                }
              }}
            >
              <ArrowLeftIcon />
            </IconButton>

            <button
              className={`
                border-none h-[36px] flex justify-center items-center font-['Inter'] font-[500] 
                text-mono-primaryTextColor hover:bg-[rgba(0,0,0,0.04)] px-[12px] text-[14px]
              `}
              onClick={handleDatePopoverClick}
            >
              {date?.format("MMM DD, YYYY")}
            </button>

            <IconButton
              sx={{
                borderRadius: "0px",
                borderTopRightRadius: "32px",
                borderBottomRightRadius: "32px"
              }}
              className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
              onClick={() => {
                if (date) {
                  const newDate = date.add(1, "day");
                  setAndStoreDate(newDate);
                }
              }}
            >
              <ArrowRightIcon />
            </IconButton>
          </div>

          {/* Popover with DateSelector */}
          <Popover
            id={datePopoverId}
            open={datePopoverOpen}
            anchorEl={dateAnchorEl}
            onClose={handleDatePopoverClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center"
            }}
            transformOrigin={{
              vertical: -25,
              horizontal: "center"
            }}
          >
            <DateSelector
              handleDatePopoverClose={handleDatePopoverClose}
              setParentDate={setAndStoreDate}
              localStorageKey="DashboardDateFilter"
              timezone={timezone}
            />
          </Popover>
        </div>

        <div className="w-full flex justify-evenly flex-wrap gap-6">
          <div className="flex-1 w-full md:w-auto min-w-full md:min-w-max h-auto md:h-[120px] rounded-lg bg-[#FAF9F7] px-4 pt-4 pb-2">
            <TitleWithImage
              icon={<SalesIcon />}
              title={translate("dashboard.sales")}
            />
            {/* <div className="mt-2 text-[48px] font-[500] leading-[53px] font-['OptimaLTStd']">
              $975
            </div> */}
            <div className="flex gap-2 md:gap-6 mt-2 md:flex-row flex-col">
              <div className="text-[48px] font-[500] leading-[53px] font-['OptimaLTStd']">
                {formatCurrency(
                  (data?.sales?.total_charged_amount ?? 0) +
                    (data?.sales?.total_tip_amount ?? 0)
                )}
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  {translate("dashboard.services")}
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  {formatCurrency(data?.sales?.total_charged_amount)}
                </div>
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  {translate("dashboard.tips")}
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  {formatCurrency(data?.sales?.total_tip_amount)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 h-[120px] rounded-lg bg-[#FAF9F7] px-4 pt-4 pb-2">
            <TitleWithImage
              icon={<PersonIcon />}
              title={translate("dashboard.customers")}
              bg="#D9ECF1"
            />
            <div className="flex gap-6 mt-2">
              <div className="text-[48px] font-[500] leading-[53px] font-['OptimaLTStd']">
                {data?.customers?.newCustomers +
                  data?.customers?.reccuringCustomers}
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  {translate("dashboard.new")}
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  {data?.customers?.newCustomers}
                </div>
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  {translate("dashboard.recurring")}
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  {data?.customers?.reccuringCustomers}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 h-[120px] rounded-lg bg-[#FAF9F7] px-4 pt-4 pb-2">
            <TitleWithImage
              icon={<ServiceDetailsIcon />}
              title={translate("dashboard.services")}
              bg="#FFDAB4"
            />
            {/* <div className="flex gap-6 mt-2">
              <div className="text-[48px] font-[500] leading-[53px] font-['OptimaLTStd']">
                24
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  Completed
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  20
                </div>
              </div>
            </div> */}
            <div className="flex gap-6 mt-2">
              <div className="text-[48px] font-[500] leading-[53px] font-['OptimaLTStd']">
                {data?.services?.total}
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  {translate("dashboard.completed")}
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  {data?.services?.completed}
                </div>
              </div>
              <div>
                <div className="text-[14px] text-[rgba(48,47,46,0.6)] font-[400] leading-[20.02px]">
                  {translate("dashboard.upcoming")}
                </div>
                <div className="text-[14px] font-[600] leading-[21.98px]">
                  {data?.services?.total - data?.services?.completed}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full h-full flex flex-col lg:grid lg:grid-cols-3 gap-6">
          <div className="w-full lg:h-[calc(100dvh-246px)] flex-1 rounded-lg lg:col-span-2">
            <DashboardAppointmentList date={date} timezone={timezone} />
          </div>
          <div className="w-full lg:h-[calc(100dvh-246px)] flex-1 rounded-lg lg:col-span-1 pb-8">
            <DashboardArtists data={data} />
          </div>
        </div>
      </div>
    </div>
  );
}
